import { LayerPanel } from '@/features/map/LayerPanel';
import { MapPin } from 'lucide-react';
import { useMemo } from 'react';
import { type ZoomTarget, useDsMap } from '../hooks/useDsMap';
import type { LiveObject } from '../utils/objectAccumulator';

const KIND_LABEL: Record<LiveObject['kind'], string> = {
  aircraft: 'Aircraft',
  drone: 'Drones',
  dot: 'Sensors',
};
const KIND_COLOUR: Record<LiveObject['kind'], string> = {
  aircraft: '#22c55e',
  drone: '#22c55e',
  dot: '#16a34a',
};

interface Props {
  objects: LiveObject[];
  zoomTarget: ZoomTarget | null;
}

export function MapPane({ objects, zoomTarget }: Props) {
  const containerRef = useDsMap(objects, zoomTarget);
  const legend = useMemo(() => {
    const counts = new Map<LiveObject['kind'], number>();
    for (const o of objects) counts.set(o.kind, (counts.get(o.kind) ?? 0) + 1);
    return [...counts];
  }, [objects]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center border-b border-(--color-border) bg-(--color-surface) px-3 py-[7px]">
        <span className="flex-1 font-sans text-[1em] font-bold text-(--color-text)">Map</span>
        <span className="font-mono text-[0.615em] text-(--color-text3)">
          {objects.length > 0 ? `${objects.length} objects` : 'No GPS'}
        </span>
      </div>
      <div className="relative flex-1">
        <div ref={containerRef} className="h-full w-full" />
        {/* Shared map customisation panel — reads/writes the same UI store as
            the main map, so the Base Map choice is unified and persists. */}
        <LayerPanel />
        {objects.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 text-(--color-text3)">
            <MapPin size={32} className="opacity-30" />
            <span className="font-sans text-[0.769em] opacity-60">
              No GPS objects on this topic
            </span>
          </div>
        )}
      </div>
      {objects.length > 0 && (
        <div className="flex min-h-[30px] flex-wrap items-center gap-1.5 border-t border-(--color-border) bg-(--color-surface) px-2.5 py-1.5">
          {legend.map(([kind, n]) => (
            <div
              key={kind}
              className="flex items-center gap-1 font-sans text-[0.769em] text-(--color-text3)"
            >
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ background: KIND_COLOUR[kind] }}
              />
              <span>
                {KIND_LABEL[kind]} · {n}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
